import { toast } from 'react-toastify';
import { onSubmitKeyhandOffProcess } from 'src/services/client';
import { useState } from 'react';

export interface KeyHandoffSignature {
  signature: string;
  signType: string;
}

export interface KeyHandoffParams {
  suiteId: string;
  notes: string;
  warrantyStartDate: string;
  internalSignature: KeyHandoffSignature;
  homeOwnerSignature: KeyHandoffSignature;
}

export interface UseKeyHandoffSubmissionReturn {
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
  refetchPageDataFromAPI: boolean;
  submitKeyHandoff: (params: KeyHandoffParams) => Promise<boolean>;
  resetState: () => void;
}

interface SubmitErrorBody {
  message?: string;
  errors?: Record<string, string[]>;
  title?: string;
}

/* The endpoint answers failures in two shapes: our own { message } for handled
 * cases (e.g. missing primary contact), and ASP.NET ProblemDetails for model
 * validation — a 400 with { errors: { field: [msg] }, title }. Pull a human
 * message out of whichever shape came back so the toast isn't a bare "(400)". */
const extractSubmitErrorMessage = (
  body: SubmitErrorBody,
  status: number
): string => {
  // legacy/workaround: the API returns a context-free "The supplied value is
  // invalid." for the warrantyStartDate field. Translate that known case into
  // actionable copy here. FE-only seam, temporary — drop once the API sends a
  // human message (or we stop submitting an empty warranty date).
  if (body?.errors?.warrantyStartDate) {
    return 'The warranty start date is likely missing or invalid — please check it and try again.';
  }

  if (body?.message) {
    return body.message;
  }

  const fieldMessages: string[] = body?.errors
    ? Object.values(body.errors).flat()
    : [];

  if (fieldMessages.length > 0) {
    return fieldMessages.join(' ');
  }

  if (body?.title) {
    return body.title;
  }

  return `Key Pickup process submit failed (${status}).`;
};

/* Custom hook for handling key handoff submission process Returns a boolean indicating success/failure
 * so components can handle post-submission logic (like closing dialogs) appropriately. */
export const useKeyHandoffSubmission = (): UseKeyHandoffSubmissionReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchPageDataFromAPI, setRefetchPageDataFromAPI] =
    useState<boolean>(false);

  const submitKeyHandoff = async (
    params: KeyHandoffParams
  ): Promise<boolean> => {
    try {
      setError(null);
      setIsSubmitting(true);

      const response: any = await onSubmitKeyhandOffProcess(params);

      if (response?.isError) {
        throw new Error(
          response.error || 'Something went wrong submitting the Key Pickup.'
        );
      }

      // legacy/workaround: apiReq is polymorphic — a raw fetch Response for
      // 2xx/5xx, but an already-parsed envelope for 400. Only call .json() on
      // the real Response; the 400 envelope is the body already.
      const isRawResponse: boolean = typeof response?.json === 'function';
      const body: any = isRawResponse ? await response.json() : response;

      if (!response.ok) {
        throw new Error(extractSubmitErrorMessage(body, response.status));
      }

      setIsSuccess(true);
      setRefetchPageDataFromAPI(true);

      return true;
    } catch (error: any) {
      console.error('Key Pickup submission error:', error);

      const errorMessage: any =
        error.message ||
        'Something went wrong with submitting the Key Pickup process';
      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });

      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetState = () => {
    setIsSubmitting(false);
    setIsSuccess(false);
    setError(null);
    setRefetchPageDataFromAPI(false);
  };

  return {
    isSubmitting,
    isSuccess,
    error,
    refetchPageDataFromAPI,
    submitKeyHandoff,
    resetState,
  };
};
